/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment } from 'react';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbList,
    BreadcrumbSeparator,
    BREADCRUMB_ENTRY_PADDING,
} from '@/components/ui/breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { BREADCRUMB_SCROLL_ROW } from './styles';
import { cn } from '@/lib/utils';

/**
 * Placeholder label widths, one per entry — a three-level trail, which is the shape the
 * three-level taxonomy (CRCB2B-260) produces. Only the WIDTHS are guesswork; the height is
 * derived, not guessed (see below).
 */
const ENTRY_WIDTHS = ['w-16', 'w-24', 'w-28'];

/**
 * Component that provides a loading state placeholder for category breadcrumbs.
 *
 * Built on the SAME primitives as the resolved `<CategoryBreadcrumbs>` — `BreadcrumbList` with
 * its `p-1 -m-1` focus-ring slack, `BreadcrumbItem`, and `BREADCRUMB_ENTRY_PADDING` around each
 * bar — so the height it reserves IS the height that arrives, rather than an approximation of
 * it. That is the whole point of this file: only the PDP renders it, because only the PDP defers
 * the category (the PLP awaits it in the loader and renders the trail directly), and on the PDP
 * it sits above the fold in a `flex flex-col gap-4` column where any difference shows as CLS.
 *
 * The height is a derivation, not a constant: a 20px label line box (`h-5`, matching the list's
 * `leading-5`) plus 8px of entry padding top and bottom is a 36px row. Measured — real trail and
 * this skeleton both occupy 36px in that column, pinned by the paired assertions in
 * `scripts/rendered-styles/breadcrumbs.spec.json`. Do NOT restate that as a hard-coded `h-9`
 * here: it would silently drift the moment the entry padding changes, and jsdom applies no
 * stylesheets so nothing in the unit suite would catch it.
 *
 * OPEN QUESTION, deliberately not addressed here: this reserves 36px, but when the deferred
 * category resolves to `undefined` (a product with no `primaryCategoryId`, and no master to
 * inherit one from) the route's `<Await>` renders nothing, so the row collapses 36px -> 0px.
 * Making this skeleton accurate makes that particular jump LARGER, not smaller. Whether the
 * breadcrumb row should hold its height unconditionally — reserving space on every PDP for a
 * trail that may never arrive — is a separate design decision, not a sizing bug. See the note at
 * the `<Suspense>` boundary in `routes/_app.product.$productId.tsx`.
 *
 * @returns A skeleton layout matching the breadcrumbs navigation structure
 */
export function CategoryBreadcrumbsSkeleton() {
    return (
        // `aria-label` overrides the primitive's own lowercase "breadcrumb" — the spread wins —
        // and is capitalised to match what the resolved trail announces via its translated label.
        // `w-full` matches the resolved trail: the placeholder bars are short enough that they
        // never reach the fit-content blowout the real trail can (see the comment on the nav in
        // `index.tsx`), but the two roots have to size identically or the swap moves the row.
        <Breadcrumb aria-label="Breadcrumb" data-testid="breadcrumbs-skeleton" className="w-full">
            {/* The SAME row treatment as the resolved trail, imported rather than restated: the
                reserved height is only the arriving height if both sides compose the row the same
                way, and a comment asking two class strings to stay character-identical is not a
                guarantee. The `p-1 -m-1` pair inside it is what makes this exact — it nets to zero
                footprint only when both halves are present, so restating half of it would change
                the reserved height. The scroll behaviour is inert here (placeholder bars never
                overflow) and comes along only for that identity.
                No `tabIndex`, unlike the resolved trail's one-entry case: the placeholder bars
                leave a 9px scrollable delta — the row's own trailing padding, not trail
                overflow — which is below the 13px axe treats as a scrollable region, and is not a
                scroll anyone needs to perform. */}
            <BreadcrumbList className={BREADCRUMB_SCROLL_ROW}>
                {ENTRY_WIDTHS.map((width, index) => (
                    <Fragment key={width}>
                        {index > 0 && (
                            // The real separator is a text solidus; during loading it is a bar of
                            // the same width, so the row reads as placeholder rather than as a
                            // half-loaded trail. It is shorter than an entry and never drives the
                            // row height.
                            <BreadcrumbSeparator className="shrink-0">
                                <Skeleton className="h-5 w-2" />
                            </BreadcrumbSeparator>
                        )}
                        {/* Every entry is `shrink-0`, as in the resolved trail: nothing shrinks to
                            absorb overflow, it scrolls instead. Placeholder bars are short enough
                            that they never reach that point. */}
                        <BreadcrumbItem className="shrink-0">
                            {/* The entry padding is what makes this 36px rather than 20px. */}
                            <span className={BREADCRUMB_ENTRY_PADDING}>
                                <Skeleton className={cn('h-5', width)} />
                            </span>
                        </BreadcrumbItem>
                    </Fragment>
                ))}
            </BreadcrumbList>
        </Breadcrumb>
    );
}
